/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, Typography, Box, MenuItem, Select, InputLabel, SelectChangeEvent, FormControl, CardContent, TextField, useTheme, Checkbox, FormControlLabel, Snackbar, Alert, SnackbarCloseReason, LinearProgress, Paper, alpha, IconButton, DialogTitle, Grid, Backdrop, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { FaturamentoService } from '../../../shared/services/api/Financeiro/Faturamento';
import * as yup from 'yup';
import { AutoCompleteContaBancariaSimple } from '../../../shared/forms';
import { Environment } from '../../../shared/environment';

export interface IPagamentoData {
  id: string;
  idconta: string;
  cpf: string;
  nome: string;
  indpag: number;
  tpag: string;
  tcnpj?: string;
  tband?: string;
  caut?: string;
  promocao: boolean;
}
export interface PaymentDialogProps {
  open: boolean;
  selectedId: string | null;
  selectedtpag?: number;
  onClose: (value?: string) => void;
  incluirCpf: boolean;
  numerodocumento?: number;
  valortotal?: number;
  parceironome?: string;
  emissaodata?: string;
}

// Componente de Loading de Tela Cheia escuro
const FullScreenLoading: React.FC<{ message?: string; progress?: number }> = ({
  message = "Emitindo nota fiscal...",
  progress
}) => {
  return (
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}
      open={true}
    >
      <CircularProgress
        size={60}
        sx={{
          color: 'white',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          }
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: 'white',
          fontWeight: 500,
          textAlign: 'center'
        }}
      >
        {message}
      </Typography>
      {progress !== undefined && progress > 0 && (
        <Box sx={{ width: '300px', mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, display: 'block', textAlign: 'center' }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      )}
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          textAlign: 'center'
        }}
      >
        Aguarde enquanto processamos sua solicitação...
      </Typography>
    </Backdrop>
  );
};

// Função para validar CPF
const validarCPF = (cpf: string): boolean => {
  cpf = cpf.replace(/[^\d]/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = 11 - (soma % 11);
  let digitoVerificador = resto === 10 || resto === 11 ? 0 : resto;
  if (digitoVerificador !== parseInt(cpf.charAt(9))) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = 11 - (soma % 11);
  digitoVerificador = resto === 10 || resto === 11 ? 0 : resto;
  if (digitoVerificador !== parseInt(cpf.charAt(10))) return false;

  return true;
};

const formatarCPF = (cpf: string): string => {
  cpf = cpf.replace(/[^\d]/g, '');
  if (cpf.length <= 3) return cpf;
  if (cpf.length <= 6) return cpf.replace(/(\d{3})(\d+)/, '$1.$2');
  if (cpf.length <= 9) return cpf.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d+)/, '$1.$2.$3-$4');
};

const formatCurrency = (value?: number) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return dateString;
  }
};

const getValidationSchema = (incluirCpf: boolean, isPagamentoCartao: boolean, isPagamentoPix: boolean) => {
  const schemaShape: any = {
    id: yup.string().required('O ID é obrigatório'),
    indpag: yup.number().required('Informe o tipo de pagamento'),
    idconta: yup.string().required('Cadastre uma conta Padrão bancária'),
    tpag: yup.string().required('Selecione a forma de pagamento'),
    promocao: yup.boolean().required(),
    tband: yup.string().optional(),
    caut: yup.string().optional(),
    cpf: yup.string().optional(),
    tcnpj: yup.string().optional(),
    nome: yup.string().optional(),
  };

  if (isPagamentoCartao) {
    schemaShape.tband = yup.string().required('Bandeira do cartão é obrigatória');
    schemaShape.caut = yup.string().required('Código de autorização é obrigatório');
  }

  if (isPagamentoPix) {
    schemaShape.caut = yup.string().required('ID da transação PIX é obrigatório');
  }

  if (incluirCpf) {
    schemaShape.cpf = yup
      .string()
      .required('CPF é obrigatório')
      .test('cpf-valid', 'CPF inválido', (value) => {
        if (!value) return false;
        const cpfLimpo = value.replace(/[^\d]/g, '');
        if (cpfLimpo.length === 14) return false;
        return validarCPF(cpfLimpo);
      })
      .test('not-cnpj', 'CNPJ não é permitido, informe um CPF válido', (value) => {
        if (!value) return false;
        const cpfLimpo = value.replace(/[^\d]/g, '');
        if (cpfLimpo.length === 14) return false;
        return cpfLimpo.length === 11;
      });

    schemaShape.nome = yup
      .string()
      .required('Nome é obrigatório')
      .min(3, 'Nome deve ter no mínimo 3 caracteres')
      .max(100, 'Nome deve ter no máximo 100 caracteres');
  }

  return yup.object().shape(schemaShape);
};

export const Dialogsendnfce: React.FC<PaymentDialogProps> = (props) => {
  const { onClose, open, selectedId, selectedtpag, incluirCpf, numerodocumento, valortotal, parceironome, emissaodata } = props;
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Emitindo nota fiscal...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [idcontaError, setIdcontaError] = useState('');

  // Estados para o visualizador de PDF
  const [openPdfViewer, setOpenPdfViewer] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const pagamentoInicial: IPagamentoData = {
    id: '',
    indpag: 0,
    tpag: '01',
    cpf: '',
    nome: '',
    tband: '',
    caut: '',
    promocao: false,
    tcnpj: '',
    idconta: Environment.URL_IDCONTA || '',
  };

  const [pagamento, setPagamento] = useState<IPagamentoData>(pagamentoInicial);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('info');
  const [cpfError, setCpfError] = useState('');
  const [nomeError, setNomeError] = useState('');
  const [tbandError, setTbandError] = useState('');
  const [cautError, setCautError] = useState('');

  // Simular progresso
  const simulateProgress = () => {
    setLoadingProgress(0);
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 800);
    return () => clearInterval(interval);
  };
  const promocaoHabilitada = pagamento.tpag === '01';
  const resetDialog = () => {
    setPagamento({
      ...pagamentoInicial,
      id: selectedId || '',
    });
    setMsg('');
    setTms('info');
    setOpenmsg(false);
    setCpfError('');
    setNomeError('');
    setTbandError('');
    setCautError('');
    setIdcontaError('');
    setShowFullScreenLoading(false);
    setLoadingProgress(0);
    setLoadingMessage('Emitindo nota fiscal...');
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    setMsg('');
    setTms('info');
  };

  // Função para fechar o visualizador de PDF
  const handleClosePdfViewer = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setOpenPdfViewer(false);
    // Fecha o dialog principal após fechar o visualizador
    onClose();
  };

  // Função para download do PDF
  const handleDownloadPdf = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = `nfce_${selectedId}.pdf`;
      link.click();
    }
  };

  // Função para imprimir
  const handlePrintPdf = () => {
    if (pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl, '_blank');
      printWindow?.print();
    }
  };

  useEffect(() => {
    if (open) {
      resetDialog();
    }
  }, [open]);

  useEffect(() => {
    if (selectedId) {
      setPagamento((prev) => ({ ...prev, id: selectedId }));
    }
  }, [selectedId, open]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const tiposPagamento = [
    { id: 0, nome: 'À vista', icone: '💰' },
    { id: 1, nome: 'Prazo', icone: '💳' },
  ];

  const todasFormasPagamento = [
    { id: '01', nome: 'Dinheiro' },
    { id: '17', nome: 'PIX Dinâmico' },
    { id: '20', nome: 'PIX Estático' },
    { id: '04', nome: 'Cartão de Débito' },
    { id: '03', nome: 'Cartão de Crédito' },
    { id: '90', nome: 'Sem Pagamento' },
  ];

  const bandeirasCartao = [
    { id: '01', nome: 'Visa', cor: '#1a1f71' },
    { id: '02', nome: 'Mastercard', cor: '#eb001b' },
    { id: '06', nome: 'Elo', cor: '#00a4e0' },
    { id: '07', nome: 'Hipercard', cor: '#f47b00' },
    { id: '03', nome: 'American Express', cor: '#2e77bc' },
    { id: '99', nome: 'Outros', cor: '#666666' },
  ];

  const isPagamentoCartao = pagamento.tpag === '03' || pagamento.tpag === '04';
  const isPagamentoPix = pagamento.tpag === '17';
  const shouldShowBandeira = isPagamentoCartao;
  const shouldShowCaut = isPagamentoCartao || isPagamentoPix;

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = formatarCPF(e.target.value);
    setPagamento({ ...pagamento, cpf: value });
    if (cpfError) setCpfError('');

    const cpfLimpo = value.replace(/[^\d]/g, '');
    if (cpfLimpo.length === 14) {
      setCpfError('CNPJ não é permitido. Informe um CPF válido');
      return;
    }

    if (cpfLimpo.length === 11) {
      if (!validarCPF(cpfLimpo)) {
        setCpfError('CPF inválido');
      } else {
        setCpfError('');
      }
    } else if (cpfLimpo.length > 0 && cpfLimpo.length !== 11) {
      setCpfError('CPF deve conter 11 dígitos');
    } else {
      setCpfError('');
    }
  };

  const handleNomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPagamento({ ...pagamento, nome: value });
    if (nomeError) setNomeError('');

    if (value && value.length < 3) {
      setNomeError('Nome deve ter no mínimo 3 caracteres');
    } else if (value && value.length > 100) {
      setNomeError('Nome deve ter no máximo 100 caracteres');
    } else {
      setNomeError('');
    }
  };

  const handleBandeiraChange = (value: string) => {
    setPagamento({ ...pagamento, tband: value });
    if (tbandError) setTbandError('');
  };

  const handleCautChange = (value: string) => {
    setPagamento({ ...pagamento, caut: value });
    if (cautError) setCautError('');

    if (isPagamentoCartao && !value) {
      setCautError('Código de autorização é obrigatório');
    } else if (isPagamentoPix && !value) {
      setCautError('ID da transação PIX é obrigatório');
    } else {
      setCautError('');
    }
  };

  const validateFields = (): boolean => {
    let isValid = true;

    if (isPagamentoCartao) {
      if (!pagamento.tband || pagamento.tband === '00') {
        setTbandError('Bandeira do cartão é obrigatória');
        isValid = false;
      }
      if (!pagamento.caut) {
        setCautError('Código de autorização é obrigatório');
        isValid = false;
      }
    }

    if (isPagamentoPix && !pagamento.caut) {
      setCautError('ID da transação PIX é obrigatório');
      isValid = false;
    }

    if (incluirCpf) {
      if (!pagamento.cpf) {
        setCpfError('CPF é obrigatório');
        isValid = false;
      } else {
        const cpfLimpo = pagamento.cpf.replace(/[^\d]/g, '');
        if (cpfLimpo.length === 14) {
          setCpfError('CNPJ não é permitido. Informe um CPF válido');
          isValid = false;
        } else if (cpfLimpo.length !== 11) {
          setCpfError('CPF deve conter 11 dígitos');
          isValid = false;
        } else if (!validarCPF(cpfLimpo)) {
          setCpfError('CPF inválido');
          isValid = false;
        }
      }

      if (!pagamento.nome) {
        setNomeError('Nome é obrigatório');
        isValid = false;
      } else if (pagamento.nome.length < 3) {
        setNomeError('Nome deve ter no mínimo 3 caracteres');
        isValid = false;
      } else if (pagamento.nome.length > 100) {
        setNomeError('Nome deve ter no máximo 100 caracteres');
        isValid = false;
      }
    }

    return isValid;
  };

  const handleConfirmarPagamento = async (dados: IPagamentoData) => {
    let progressCleanup: (() => void) | null = null;

    try {
      setIsLoading(true);
      setShowFullScreenLoading(true);
      setLoadingMessage('Validando dados da nota fiscal...');

      progressCleanup = simulateProgress();

      if (!validateFields()) {
        setIsLoading(false);
        setShowFullScreenLoading(false);
        return;
      }

      setLoadingMessage('Enviando para SEFAZ...');
      setLoadingProgress(40);

      const validationSchema = getValidationSchema(incluirCpf, isPagamentoCartao, isPagamentoPix);
      const dadosValidados = await validationSchema.validate(dados, {
        abortEarly: false,
        stripUnknown: true,
      });

      setLoadingMessage('Processando autorização da nota...');
      setLoadingProgress(60);

      const dadosCompletos = {
        id: dadosValidados.id,
        idconta: dadosValidados.idconta,
        tcnpj: dadosValidados.tcnpj || '',
        faturado: false,
        indpag: dadosValidados.indpag,
        tpag: dadosValidados.tpag,
        promocao: dadosValidados.tpag === '01'
          ? dadosValidados.promocao
          : false,
        tband: shouldShowBandeira ? dadosValidados.tband || '' : '',
        caut: shouldShowCaut ? dadosValidados.caut || '' : '',
        cpf: incluirCpf ? dados.cpf?.replace(/[^\d]/g, '') : '',
        nome: incluirCpf ? dados.nome : '',
      };

      setLoadingMessage('Gerando NFC-e...');
      setLoadingProgress(80);

      const resultado = await FaturamentoService.createnfce(dadosCompletos);

      if (!(resultado instanceof Blob)) {
        throw new Error('Arquivo da NFC-e inválido.');
      }

      setLoadingMessage('Nota autorizada! Preparando visualização...');
      setLoadingProgress(100);

      // Pequeno delay para o usuário ver o 100%
      await new Promise(resolve => setTimeout(resolve, 500));

      // Criar URL do blob e abrir no visualizador
      const url = URL.createObjectURL(resultado);
      setPdfBlobUrl(url);
      setOpenPdfViewer(true);

      setShowFullScreenLoading(false);
      setTms('success');
      setMsg('NFC-e gerada com sucesso!');
      setOpenmsg(true);

    } catch (error: any) {
      console.error('Erro na emissão:', error);
      setShowFullScreenLoading(false);

      setTms('error');
      if (error.name === 'ValidationError') {
        setMsg(error.errors.join('\n'));
      } else {
        setMsg(error.message || 'Erro inesperado ao gerar NFC-e.');
      }
      setOpenmsg(true);
    } finally {
      setIsLoading(false);
      if (progressCleanup) {
        progressCleanup();
      }
    }
  };

  const handleCancel = () => {
    if (showFullScreenLoading) {
      return;
    }
    resetDialog();
    onClose();
  };

  const getAdditionalDataConfig = () => {
    if (isPagamentoCartao) {
      return {
        title: '💳 Dados do Cartão (Obrigatório)',
        color: theme.palette.primary.main,
        required: true,
      };
    } else if (isPagamentoPix) {
      return {
        title: '💳 Dados do PIX (Obrigatório)',
        color: theme.palette.success.main,
        required: true,
      };
    }
    return null;
  };

  const additionalConfig = getAdditionalDataConfig();

  const isConfirmDisabled = () => {
    if (isLoading || showFullScreenLoading) return true;
    if (selectedtpag === 1 && !pagamento.idconta) return true;
    if (isPagamentoCartao) {
      if (!pagamento.tband || pagamento.tband === '00' || !pagamento.caut) return true;
    }

    if (isPagamentoPix && !pagamento.caut) return true;

    if (incluirCpf) {
      const cpfLimpo = pagamento.cpf?.replace(/[^\d]/g, '') || '';
      if (cpfLimpo.length !== 11 || !validarCPF(cpfLimpo) || !pagamento.nome || pagamento.nome.length < 3) {
        return true;
      }
    }

    return false;
  };

  return (
    <>
      {showFullScreenLoading && (
        <FullScreenLoading
          message={loadingMessage}
          progress={loadingProgress}
        />
      )}

      <Dialog
        open={open && !openPdfViewer}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        <Box
          sx={{
            p: 2.5,
            pb: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main
              }}
            >
              <ReceiptIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                Emitir NFC-e
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Nota Fiscal de Consumidor Eletrônica
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCancel}
            disabled={showFullScreenLoading}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: alpha(theme.palette.text.primary, 0.05)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.default, 0.4)
          }}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Nº Documento
              </Typography>
              <Typography variant="body2" fontWeight="600">
                #{numerodocumento || '—'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Data Emissão
              </Typography>
              <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.75rem' }}>
                {formatDate(emissaodata)}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Total
              </Typography>
              <Typography variant="body2" fontWeight="700" color="success.main" sx={{ fontSize: '0.85rem' }}>
                {formatCurrency(valortotal)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Cliente
              </Typography>
              <Typography
                variant="body2"
                fontWeight="500"
                sx={{
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {parceironome || '—'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <DialogContent dividers sx={{ p: 0 }}>
          {isLoading && !showFullScreenLoading && (
            <LinearProgress
              color="primary"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 2,
              }}
            />
          )}

          <CardContent sx={{ p: 2.5 }}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel shrink sx={{ backgroundColor: theme.palette.background.paper, px: 1, fontSize: '0.75rem' }}>
                Tipo de Pagamento
              </InputLabel>
              <Select
                size="small"
                value={pagamento.indpag}
                onChange={(e: SelectChangeEvent<number>) =>
                  setPagamento({ ...pagamento, indpag: Number(e.target.value) })
                }
                sx={{ fontSize: '0.875rem' }}
                disabled={showFullScreenLoading}
              >
                {tiposPagamento.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontSize="1rem">{tipo.icone}</Typography>
                      <Typography fontSize="0.875rem">{tipo.nome}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel shrink sx={{ backgroundColor: theme.palette.background.paper, px: 1, fontSize: '0.75rem' }}>
                Forma de Pagamento
              </InputLabel>
              <Select
                size="small"
                value={pagamento.tpag}
                onChange={(e) => {
                  setPagamento({
                    ...pagamento,
                    tpag:  e.target.value,
                    tband: '',
                    caut: '',
                    promocao: e.target.value === '01' ? pagamento.promocao : false,
                  });
                  setTbandError('');
                  setCautError('');
                }}
                sx={{ fontSize: '0.875rem' }}
                disabled={showFullScreenLoading}
              >
                {todasFormasPagamento.map((forma) => (
                  <MenuItem key={forma.id} value={forma.id}>
                    <Typography fontSize="0.875rem">{forma.nome}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedtpag === 1 && (
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <AutoCompleteContaBancariaSimple
                    label='Conta'
                    value={pagamento.idconta}
                    onChange={(idconta) => {
                      setPagamento({ ...pagamento, idconta });
                      setIdcontaError('');
                    }}
                    isExternalLoading={isLoading}
                    disabled={showFullScreenLoading}
                    error={!!idcontaError}
                    helperText={idcontaError}
                  />
                </Grid>
              </Grid>
            )}
            {additionalConfig && shouldShowCaut && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 2,
                  bgcolor: alpha(additionalConfig.color, 0.04),
                  borderRadius: 2,
                  border: `1px solid ${alpha(additionalConfig.color, 0.1)}`
                }}
              >
                <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 600, color: additionalConfig.color, display: 'block' }}>
                  {additionalConfig.title} <span style={{ color: '#d32f2f' }}>*</span>
                </Typography>

                {shouldShowBandeira && (
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }} required>
                    <InputLabel shrink sx={{ fontSize: '0.7rem' }}>Bandeira</InputLabel>
                    <Select
                      size="small"
                      value={pagamento.tband || ''}
                      onChange={(e) => handleBandeiraChange(e.target.value)}
                      sx={{ fontSize: '0.875rem' }}
                      error={!!tbandError}
                      disabled={showFullScreenLoading}
                    >
                      <MenuItem value="">
                        <em>Selecione a bandeira</em>
                      </MenuItem>
                      {bandeirasCartao.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: b.cor }} />
                            <Typography fontSize="0.875rem">{b.nome}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {tbandError && (
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          ml: 1.5,
                          color: '#000000',
                          backgroundColor: alpha(theme.palette.error.main, 0.08),
                          padding: '4px 8px',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.7rem',
                          fontWeight: 500
                        }}
                      >
                        <span>⚠️</span> {tbandError}
                      </Typography>
                    )}
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  size="small"
                  label={isPagamentoPix ? "ID da Transação PIX" : "Código de Autorização"}
                  value={pagamento.caut}
                  onChange={(e) => handleCautChange(e.target.value)}
                  placeholder={isPagamentoPix ? "Digite o ID da transação" : "Digite o código de autorização"}
                  required
                  error={!!cautError}
                  helperText={cautError}
                  disabled={showFullScreenLoading}
                  FormHelperTextProps={{
                    sx: {
                      color: '#000000',
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      padding: '4px 8px',
                      borderRadius: 1,
                      marginTop: 1,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      '&::before': {
                        content: '"⚠️ "',
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': { fontSize: '0.875rem' },
                  }}
                />
              </Paper>
            )}

            {incluirCpf && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
                }}
              >
                <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 600, color: theme.palette.info.main, display: 'block' }}>
                  👤 Dados do Cliente <span style={{ color: '#d32f2f' }}>*</span>
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  label="CPF"
                  value={pagamento.cpf}
                  onChange={handleCpfChange}
                  placeholder="000.000.000-00"
                  required
                  error={!!cpfError}
                  helperText={cpfError}
                  disabled={showFullScreenLoading}
                  FormHelperTextProps={{
                    sx: {
                      color: '#000000',
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      padding: '4px 8px',
                      borderRadius: 1,
                      marginTop: 1,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      '&::before': {
                        content: '"⚠️ "',
                      }
                    }
                  }}
                  sx={{
                    mb: 1.5,
                    '& .MuiInputBase-root': { fontSize: '0.875rem' },
                  }}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Nome Completo"
                  value={pagamento.nome}
                  onChange={handleNomeChange}
                  placeholder="Digite o nome completo"
                  required
                  error={!!nomeError}
                  helperText={nomeError}
                  disabled={showFullScreenLoading}
                  FormHelperTextProps={{
                    sx: {
                      color: '#000000',
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      padding: '4px 8px',
                      borderRadius: 1,
                      marginTop: 1,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      '&::before': {
                        content: '"⚠️ "',
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': { fontSize: '0.875rem' },
                  }}
                />
              </Paper>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={pagamento.promocao}
                  onChange={(e) =>
                    setPagamento({ ...pagamento, promocao: e.target.checked })
                  }
                  disabled={!promocaoHabilitada || showFullScreenLoading}
                  sx={{
                    color: theme.palette.warning.main,
                    '&.Mui-checked': {
                      color: theme.palette.warning.main,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: promocaoHabilitada
                      ? 'text.primary'
                      : 'text.disabled'
                  }}
                >
                  Aplicar promoção
                </Typography>
              }
            />
          </CardContent>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1.5, bgcolor: theme.palette.background.default }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            color="inherit"
            size="small"
            disabled={showFullScreenLoading}
            sx={{
              flex: 1,
              py: 0.8,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem'
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleConfirmarPagamento(pagamento)}
            variant="contained"
            color="primary"
            disabled={isConfirmDisabled()}
            size="small"
            sx={{
              flex: 1,
              py: 0.8,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
              }
            }}
          >
            {isLoading ? 'Processando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visualizador de PDF da NFC-e */}
      <Dialog
        open={openPdfViewer}
        onClose={handleClosePdfViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PictureAsPdfIcon color="error" />
            NFC-e - Documento Fiscal Eletrônico
          </Typography>
          <Box>
            <IconButton onClick={handleDownloadPdf} sx={{ mr: 1 }} title="Download">
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={handlePrintPdf} sx={{ mr: 1 }} title="Imprimir">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={handleClosePdfViewer}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
          {isGeneratingPdf || !pdfBlobUrl ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400, flexDirection: 'column', gap: 2 }}>
              <LinearProgress sx={{ width: '80%' }} />
              <Typography>Gerando NFC-e...</Typography>
            </Box>
          ) : (
            <iframe
              src={pdfBlobUrl}
              style={{
                width: '100%',
                height: 'calc(90vh - 120px)',
                border: 'none',
              }}
              title="Visualizador de NFC-e"
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'success' ? 2000 : null}
        onClose={handleCloseMsg}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          elevation={6}
          sx={{
            width: '100%',
            minWidth: '280px',
            borderRadius: 2,
            '& .MuiAlert-message': {
              flex: 1,
              py: 0.5,
            },
            '& .MuiAlert-icon': {
              fontSize: '1.2rem',
              alignItems: 'center',
            },
            ...(tms === 'error' && {
              backgroundColor: '#d32f2f',
            }),
            ...(tms === 'success' && {
              backgroundColor: '#2e7d32',
            }),
            ...(tms === 'warning' && {
              backgroundColor: '#ed6c02',
            }),
            ...(tms === 'info' && {
              backgroundColor: '#0288d1',
            }),
          }}
          action={
            tms !== 'success' ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleCloseMsg}
                sx={{
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                FECHAR
              </Button>
            ) : null
          }
        >
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.3, color: '#FFFFFF' }}>
              {tms === 'error' ? '❌ ERRO' :
                tms === 'warning' ? '⚠️ ATENÇÃO' :
                  tms === 'success' ? '✅ SUCESSO' : 'ℹ️ INFORMAÇÃO'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4, color: '#FFFFFF' }}>
              {msg}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
};